import { ECalendarView, IActivityTime } from '../../../types';
import { IColors } from '../../../../types';
import {
	convertTimeRangeToMinutes,
	DEFAULT_NEW_SLOT_DURATION,
	formatWallClockMinutesAs24H,
} from '../../../utils/timeUtils';
import { getNumberOfSlotsByDuration, getSizeOfSlots } from '../../../utils';
import { useMiddlewareContext } from '../../../hooks/useMiddlewareContext';
import { useTheme } from '@emotion/react';
import { useMemo } from 'react';

type ColorRange = {
	color?: string;
	from: number;
	to: number;
};

const percent = (minutes: number, start: number, total: number): number => {
	return ((minutes - start) / total) * 100;
};

/**
 * Builds the gradient stops for the availability gradient.
 * @param currentStartTime {string} currentStartTime
 * @param currentEndTime {string} currentEndTime
 * @param availableRanges {IActivityTime[]} availableRanges
 * @param colors {IColors} colors
 * @return {string[]} css gradient stops
 */
const buildAvailabilityGradientStops = (
	currentStartTime: string,
	currentEndTime: string,
	availableRanges: IActivityTime[],
	colors: IColors
): string[] => {
	const { start: slotStartMin, end: slotEndMin } = convertTimeRangeToMinutes({
		start: currentStartTime,
		end: currentEndTime,
	});

	const totalMinutes: number = slotEndMin - slotStartMin;
	const ranges: ColorRange[] = [];
	let cursor: number = slotStartMin;

	for (const { open, close } of availableRanges) {
		const { start: availableStartMin, end: availableEndMin } = convertTimeRangeToMinutes({
			start: open,
			end: close,
		});

		if (availableEndMin <= slotStartMin || availableStartMin >= slotEndMin) continue;

		// Unavailable before available
		if (availableStartMin > cursor) {
			ranges.push({
				color: colors.bg_unavailable,
				from: percent(cursor, slotStartMin, totalMinutes),
				to: percent(Math.min(availableStartMin, slotEndMin), slotStartMin, totalMinutes),
			});
		}

		// Available range
		const startInRange: number = Math.max(availableStartMin, slotStartMin);
		const endInRange: number = Math.min(availableEndMin, slotEndMin);

		ranges.push({
			color: colors.bg_white,
			from: percent(startInRange, slotStartMin, totalMinutes),
			to: percent(endInRange, slotStartMin, totalMinutes),
		});

		cursor = Math.max(cursor, endInRange);
	}

	if (cursor < slotEndMin) {
		ranges.push({
			color: colors.bg_unavailable,
			from: percent(cursor, slotStartMin, totalMinutes),
			to: 100,
		});
	}

	return ranges.flatMap(({ color, from, to }) => [`${color} ${from}%`, `${color} ${to}%`]);
};

/**
 * Calculates the gradient for the given activity times.
 * @param activityTimes {IActivityTime[]} activityTimes
 * @param length {number} length
 * @param duration {number} duration
 * @param colors {IColors} colors
 * @param horizontal {boolean} horizontal
 * @return {string[]} css gradients
 */
const calculateGradient = (
	activityTimes: IActivityTime[],
	length: number,
	duration: number,
	colors: IColors,
	horizontal: boolean = false
): string[] => {
	return Array.from({ length }, (_, idx) => {
		const formattedStart: string = formatWallClockMinutesAs24H(idx * duration);
		const formattedEnd: string = formatWallClockMinutesAs24H((idx + 1) * duration);

		const stops: string[] = buildAvailabilityGradientStops(formattedStart, formattedEnd, activityTimes, colors);

		return `linear-gradient(${horizontal ? 'to right' : 'to bottom'}, ${stops.join(', ')})`;
	});
};

type Props = {
	activityTimes: IActivityTime[];
	hourSize: number;
	colors: IColors;
};

export const useActivityTimeGradients = ({ activityTimes, hourSize, colors }: Props) => {
	const { options } = useMiddlewareContext();

	const horizontal: boolean = options.view === ECalendarView.HORIZONTAL;

	const duration: number = options.addNewSlotButton?.duration ?? DEFAULT_NEW_SLOT_DURATION;

	const numberOfSlots: number = getNumberOfSlotsByDuration(duration);

	const size: number = getSizeOfSlots(hourSize, numberOfSlots);

	const gradients: string[] = useMemo(() => {
		return calculateGradient(activityTimes, numberOfSlots, duration, colors, horizontal);
	}, [activityTimes, numberOfSlots, duration, colors, horizontal]);

	return {
		gradients,
		duration,
		size,
	};
};
